// ---------------------------------------------------------------------------
// Printed-footprint maths for label elements.
//
// Pure, no React: the canvas, the properties panel and the overflow warnings all
// need to agree on how big a thing will actually print, so the sizing lives here
// rather than in any one component.
// ---------------------------------------------------------------------------

import QRCode from "qrcode";
import { DPMM_203, fontByKey } from "../labelSchema";
import { measureSystemTextDots } from "./textRaster";

/**
 * How many modules wide the symbol is for this payload + error-correction level.
 * The printer picks the same version from the same inputs, so this is what lets the
 * preview show the true printed footprint (modules x magnification dots).
 */
export function qrModuleCount(data, ecLevel) {
  try {
    return QRCode.create(String(data || " "), { errorCorrectionLevel: ecLevel }).modules.size;
  } catch (e) {
    return 21; // version 1 — payload too long to encode; caller shows the warning
  }
}

/** True when the payload cannot be encoded at all at this error-correction level. */
export function qrPayloadTooLong(data, ecLevel) {
  try {
    QRCode.create(String(data || " "), { errorCorrectionLevel: ecLevel });
    return false;
  } catch (e) {
    return true;
  }
}

/** Footprint of an element on the label, in mm — the same maths the ZPL uses. */
export function elementSizeMm(el, dpmm = DPMM_203) {
  if (el.type === "qr") {
    const mm = (qrModuleCount(el.text, el.ecLevel) * el.magnification) / dpmm;
    return { wMm: mm, hMm: mm };
  }
  // ^A0's height IS the character cell and ^FB stacks lines by exactly that, so
  // there is no extra leading to allow for.
  return { wMm: el.widthMm, hMm: (el.fontH * el.maxLines) / dpmm };
}

/** True when the element sticks out past the label edge and would be clipped. */
export function overflowsLabel(el, label) {
  const { wMm, hMm } = elementSizeMm(el, label.dpmm);
  return (
    el.xMm < 0 ||
    el.yMm < 0 ||
    el.xMm + wMm > label.widthMm + 0.01 ||
    el.yMm + hMm > label.heightMm + 0.01
  );
}

/** Do two elements' printed footprints intersect? */
export function elementsOverlap(a, b, dpmm = DPMM_203) {
  const sa = elementSizeMm(a, dpmm);
  const sb = elementSizeMm(b, dpmm);
  return (
    a.xMm < b.xMm + sb.wMm &&
    b.xMm < a.xMm + sa.wMm &&
    a.yMm < b.yMm + sb.hMm &&
    b.yMm < a.yMm + sa.hMm
  );
}

/** Every overlapping pair, as [idA, idB]. */
export function overlappingPairs(elements, dpmm = DPMM_203) {
  const out = [];
  for (let i = 0; i < elements.length; i++) {
    for (let j = i + 1; j < elements.length; j++) {
      if (elementsOverlap(elements[i], elements[j], dpmm)) {
        out.push([elements[i].id, elements[j].id]);
      }
    }
  }
  return out;
}

/** The usable box inside the label, after padding. */
export function contentBox(label) {
  const p = label.paddingMm || 0;
  return {
    left: p,
    top: p,
    right: label.widthMm - p,
    bottom: label.heightMm - p,
  };
}

/** Align one element within the padded content box. */
export function alignedPosition(el, label, how) {
  const s = elementSizeMm(el, label.dpmm);
  const b = contentBox(label);
  const map = {
    left: { xMm: b.left },
    centerX: { xMm: (label.widthMm - s.wMm) / 2 },
    right: { xMm: b.right - s.wMm },
    top: { yMm: b.top },
    centerY: { yMm: (label.heightMm - s.hMm) / 2 },
    bottom: { yMm: b.bottom - s.hMm },
  };
  const out = map[how];
  if (!out) throw new Error("Unknown alignment: " + how);
  return Object.keys(out).reduce((acc, k) => {
    acc[k] = Number(out[k].toFixed(2));
    return acc;
  }, {});
}

/**
 * Width the printer will need for a line of text, in dots.
 *
 * ^A0 is a proportional scalable font, so character width varies. Measuring with a
 * canvas in a comparable sans face is close enough to decide whether a line fits —
 * the alternative is a per-glyph table per font, which the printer does not expose.
 */
let measureCtx = null;
export function estimateTextWidthDots(text, el) {
  const s = String(text == null ? "" : text);
  if (!s) return 0;
  const fontH = el.fontH;

  // A system font is drawn by us, so the same canvas that renders it measures it.
  if (el.fontSource === "system") return measureSystemTextDots(s, el);

  // Bitmap fonts are monospace, so their width is exact rather than estimated.
  const font = fontByKey(el.fontKey);
  if (!font.scalable) {
    const cell = el.fontW || font.baseW * Math.max(1, Math.round(fontH / font.baseH));
    return s.length * cell;
  }

  try {
    if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
    // Measure at a large size and scale down, so rounding at small sizes does not
    // dominate the estimate.
    const probe = 100;
    measureCtx.font = `${probe}px Helvetica, Arial, sans-serif`;
    return (measureCtx.measureText(s).width / probe) * fontH;
  } catch (e) {
    return s.length * fontH * 0.6; // headless fallback
  }
}

/**
 * Does this text overrun its ^FB block?
 *
 * This matters because of how ZPL fails: when a ^FB block is too narrow, the text
 * wraps, and anything past the line limit is printed ON TOP of the last line rather
 * than being dropped. On the label that reads as doubled, unreadable text.
 */
export function textOverflowsBlock(el, dpmm = DPMM_203) {
  if (el.type !== "text") return false;
  const blockDots = el.widthMm * dpmm;
  const needed = estimateTextWidthDots(el.text, el);
  return needed > blockDots * Math.max(1, el.maxLines) + 1;
}

/** Block width in mm that would hold this text on one line. */
export function widthToFitTextMm(el, dpmm = DPMM_203) {
  const needed = estimateTextWidthDots(el.text, el);
  return Number((needed / dpmm + 0.4).toFixed(2)); // small allowance for hinting
}

/**
 * Lay out text the way ^FB does, so the preview can show the real printed result.
 *
 * ZPL wraps on word boundaries inside the block width. A word longer than the block
 * is broken mid-word. Crucially, when the text needs more lines than the field
 * allows, the printer does NOT drop the remainder — it prints it over the last line.
 * That overprinting is what shows up on a sticker as doubled, unreadable text, so it
 * is returned separately rather than hidden.
 *
 * @returns {{lines: string[], overprint: string[]}}
 */
export function wrapTextForFB(text, blockDots, el) {
  const src = String(text == null ? "" : text);
  const limit = Math.max(1, el.maxLines || 1);
  if (!src) return { lines: [], overprint: [] };

  const fits = (str) => estimateTextWidthDots(str, el) <= blockDots;

  const all = [];
  let line = "";

  const pushLine = () => {
    all.push(line);
    line = "";
  };

  src.split(/\s+/).filter(Boolean).forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (fits(candidate)) {
      line = candidate;
      return;
    }
    if (line) pushLine();

    // A single word wider than the block is broken, as the printer does.
    let rest = word;
    while (!fits(rest) && rest.length > 1) {
      let cut = rest.length;
      while (cut > 1 && !fits(rest.slice(0, cut))) cut--;
      all.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    line = rest;
  });
  if (line) pushLine();

  return { lines: all.slice(0, limit), overprint: all.slice(limit) };
}
